import { cn } from "@drpcrd/utils/shared";
import { Nullable } from "@drpcrd/utils";
import { formatCdnImg, getCountryFlag } from "@drpcrd/utils/formatters";
import { Avatar, AvatarFallback, AvatarImage } from "../avatar";
import { cva, type VariantProps } from "class-variance-authority";
import { User } from "lucide-react";

const avatarVariants = cva("", {
  variants: {
    size: {
      xs: "h-3 w-3",
      sm: "h-4 w-4",
      ms: "h-[28px] w-[28px]",
      md: "h-5 w-5",
      mg: "h-6 w-6",
      lg: "h-9 w-9",
    },
  },
  defaultVariants: { size: "md" },
});

type Props = VariantProps<typeof avatarVariants> & {
  creatorSrc?: Nullable<string>;
  social1Src?: Nullable<string>;
  social2Src?: Nullable<string>;
  name?: string;
  className?: string;
  countryCode?: Nullable<string>;
  showCountryFlag?: boolean;
  flagClassName?: string;
};

/**
 * Avatar component for creators that intelligently selects the best available avatar source.
 * Falls back through creator avatar -> first social avatar -> second social avatar -> default.
 * Optionally displays country flag overlay in bottom-right corner.
 */
export const CreatorAvatar = ({
  creatorSrc,
  social1Src,
  social2Src,
  name,
  size = "md",
  className,
  countryCode,
  showCountryFlag = false,
  flagClassName,
}: Props) => {
  const avatarSrc = creatorSrc ?? social1Src ?? social2Src;

  return (
    <div className={cn("relative", avatarVariants({ size }), className)}>
      <Avatar className={cn(avatarVariants({ size }), className)}>
        <AvatarImage
          src={formatCdnImg(avatarSrc ?? undefined)}
          alt={name ?? ""}
        />
        <AvatarFallback className="bg-primary-50">
          <User className="text-primary-500 h-[60%] w-[60%]" />
        </AvatarFallback>
      </Avatar>
      {showCountryFlag && countryCode && (
        <div className={flagClassName}>{getCountryFlag(countryCode)}</div>
      )}
    </div>
  );
};
